import React, { useState } from 'react';
import { ChevronDown, Paperclip, X } from 'lucide-react';
import type { UploadedFileSummary } from '../types';
import { useTranslation } from '../i18n';
import { uploadedFileReadResult } from '../services/uploadedFiles';

interface ConversationFilesProps {
  files: UploadedFileSummary[];
  onRemove: (file: UploadedFileSummary) => void;
  removeFailed: boolean;
}

const RESULT_TONE: Record<string, string> = {
  ready: 'text-green-600 dark:text-green-400',
  warning: 'text-amber-600 dark:text-amber-400',
  failed: 'text-red-600 dark:text-red-400',
};

/**
 * The files the conversation holds, above the composer.
 *
 * Every turn that continues the conversation is given all of them, the ones
 * sent with its first message as much as the ones sent with its last. So the
 * composer says how many there are, and on request which, what was read of
 * each, and takes one out.
 */
export const ConversationFiles: React.FC<ConversationFilesProps> = ({ files, onRemove, removeFailed }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // The file whose removal has been asked for and not yet confirmed.
  const [asking, setAsking] = useState<string | null>(null);

  if (files.length === 0) return null;

  const notWhole = files.filter(file => file.cardStatus !== 'ready').length;

  return (
    <div className="mb-2 text-[11px]">
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md px-1 py-0.5 text-left text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-zinc-200"
      >
        <Paperclip size={11} className="shrink-0" />
        <span>{t('Files in this conversation: {n}').replace('{n}', String(files.length))}</span>
        {notWhole > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <span className="text-amber-600 dark:text-amber-400">{t('{n} not read in full').replace('{n}', String(notWhole))}</span>
          </>
        )}
        <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        // Ten files open would stand over the conversation: the list scrolls past four.
        <ul className="custom-scrollbar mt-1.5 max-h-52 space-y-1 overflow-y-auto">
          {files.map(file => {
            const id = file.fileId || file.localId;
            const result = uploadedFileReadResult(file);
            return (
              <li
                key={id}
                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs dark:border-cyan-900/40 dark:bg-white/[0.03]"
              >
                {/* Whole, on as many lines as it takes: the name says which file, the sentence what of it was not read. */}
                <div className="min-w-0 flex-1">
                  <span className="block break-words font-medium text-gray-700 dark:text-zinc-200">{file.fileName}</span>
                  {asking === id ? (
                    // Removing cannot be taken back, and every later turn is
                    // given one file fewer: asked in the row, where the name is.
                    <>
                      <span className="block break-words text-gray-600 dark:text-zinc-300">
                        {t('Remove this file? Later turns will no longer be given it.')}
                      </span>
                      <span className="mt-1 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setAsking(null);
                            onRemove(file);
                          }}
                          className="rounded-md border border-red-300 px-2.5 py-1 font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-800/60 dark:text-red-400 dark:hover:bg-red-950/30"
                        >
                          {t('Remove')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAsking(null)}
                          className="rounded-md border border-gray-300 px-2.5 py-1 text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/15 dark:text-zinc-300 dark:hover:bg-white/5"
                        >
                          {t('Cancel')}
                        </button>
                      </span>
                    </>
                  ) : (
                    result && <span className={`block break-words ${RESULT_TONE[file.cardStatus] || ''}`}>{t(result)}</span>
                  )}
                </div>
                {asking !== id && (
                  <button
                    type="button"
                    onClick={() => setAsking(id)}
                    aria-label={`${t('Remove from this conversation')}: ${file.fileName}`}
                    title={t('Remove from this conversation')}
                    className="shrink-0 rounded-full p-0.5 text-gray-500 opacity-60 transition-opacity hover:bg-black/10 hover:opacity-100 dark:text-zinc-300 dark:hover:bg-white/10"
                  >
                    <X size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {removeFailed && (
        <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">{t('Failed to delete uploaded file')}</p>
      )}
    </div>
  );
};
